import React from "react";
import { Img, staticFile } from "remotion";

type Platform = "ios" | "android" | "web";

interface DeviceFrameProps {
  platform: Platform;
  children: React.ReactNode;
  width?: number;
  height?: number;
}

// iPhone mockup proportions (matches mockup-ios-iphone-17-pro.png from tuka)
const IOS_ASPECT = 1043 / 2063;
// Android mockup proportions (matches mockup-android-google-pixel-9-pro.png)
const ANDROID_ASPECT = 353 / 745;
const SCREEN_INSET_X = 0.027;
const SCREEN_INSET_Y = 0.014;
const SCREEN_RADIUS_RATIO = 0.13;

const IosFrame: React.FC<{ children: React.ReactNode; width: number; height: number }> = ({
  children,
  width,
  height,
}) => {
  const screenInsetX = width * SCREEN_INSET_X;
  const screenInsetY = height * SCREEN_INSET_Y;
  const screenRadius = width * SCREEN_RADIUS_RATIO;
  const screenWidth = width - screenInsetX * 2;
  const screenHeight = height - screenInsetY * 2;

  return (
    <div style={{ position: "relative", width, height }}>
      {/* Screen content */}
      <div
        style={{
          position: "absolute",
          left: screenInsetX,
          top: screenInsetY,
          width: screenWidth,
          height: screenHeight,
          borderRadius: screenRadius,
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        {children}
      </div>

      {/* PNG mockup frame overlay */}
      <Img
        src={staticFile("mockup-ios-iphone-17-pro.png")}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width,
          height,
          zIndex: 10,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

const AndroidFrame: React.FC<{ children: React.ReactNode; width: number; height: number }> = ({
  children,
  width,
  height,
}) => {
  const screenInsetX = width * 0.03;
  const screenInsetY = height * 0.016;
  const screenRadius = width * 0.10;
  const screenWidth = width - screenInsetX * 2;
  const screenHeight = height - screenInsetY * 2;

  return (
    <div style={{ position: "relative", width, height }}>
      {/* Screen content */}
      <div
        style={{
          position: "absolute",
          left: screenInsetX,
          top: screenInsetY,
          width: screenWidth,
          height: screenHeight,
          borderRadius: screenRadius,
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        {children}
      </div>

      {/* PNG mockup frame overlay */}
      <Img
        src={staticFile("mockup-android.png")}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width,
          height,
          zIndex: 10,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

const WebFrame: React.FC<{ children: React.ReactNode; width: number; height: number }> = ({
  children,
  width,
  height,
}) => {
  const titleBarHeight = 36;

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: "#1E1E2E",
        border: "1px solid #313244",
        boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Browser chrome */}
      <div
        style={{
          height: titleBarHeight,
          backgroundColor: "#181825",
          borderBottom: "1px solid #313244",
          display: "flex",
          alignItems: "center",
          padding: "0 14px",
          gap: 8,
          flexShrink: 0,
        }}
      >
        {/* Traffic lights */}
        <div style={{ display: "flex", gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#F38BA8" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#F9E2AF" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#A6E3A1" }} />
        </div>
        {/* URL bar */}
        <div
          style={{
            flex: 1,
            height: 22,
            backgroundColor: "#11111B",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            padding: "0 10px",
            fontSize: 11,
            color: "#6C7086",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          localhost:3847
        </div>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
};

export const DeviceFrame: React.FC<DeviceFrameProps> = ({
  platform,
  children,
  width,
  height,
}) => {
  if (platform === "ios") {
    const h = height || 820;
    const w = width || h * IOS_ASPECT;
    return <IosFrame width={w} height={h}>{children}</IosFrame>;
  }

  if (platform === "android") {
    const h = height || 820;
    const w = width || h * ANDROID_ASPECT;
    return <AndroidFrame width={w} height={h}>{children}</AndroidFrame>;
  }

  // Web
  const w = width || 900;
  const h = height || 580;
  return <WebFrame width={w} height={h}>{children}</WebFrame>;
};
